import { type NavigateOptions, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { shortcutKeys } from "./shortcut-keys";

// Keyboard shortcuts (design-system.md §4.10): ⌘K / Ctrl K anywhere, `g` + letter to go somewhere, `[` for the
// narrow sidebar, `?` for the list of shortcuts (ADR-114), and single keys of a screen (`/` for the search). No
// single-key shortcut fires while typing in a field or with a modifier.

export const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

const plainKey = (e: KeyboardEvent) => !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target);

/** A place of the app a `g` shortcut goes to; `to` is typed on the routes of the app that uses it. */
export interface ShortcutPlace {
  to: NonNullable<NavigateOptions["to"]>;
  key?: string | undefined;
}

interface GlobalActions {
  togglePalette: () => void;
  /** Only where the sidebar is a column (from lg). */
  toggleRail?: (() => void) | undefined;
  showShortcuts: () => void;
}

export function useGlobalShortcuts(
  { togglePalette, toggleRail, showShortcuts }: GlobalActions,
  places: readonly ShortcutPlace[],
): void {
  const navigate = useNavigate();
  useEffect(() => {
    let gPressedAt = 0;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        togglePalette();
        return;
      }
      // `[` needs AltGr or ⌥ on many layouts (Italian among them): Alt and Ctrl do not stop it
      if (e.key === shortcutKeys.rail[0] && toggleRail && !e.metaKey && !isTyping(e.target)) {
        e.preventDefault();
        toggleRail();
        return;
      }
      if (!plainKey(e)) return;
      if (e.key === shortcutKeys.help[0]) {
        e.preventDefault();
        showShortcuts();
        return;
      }
      if (e.key === "g") {
        gPressedAt = Date.now();
        return;
      }
      const place = Date.now() - gPressedAt < 1000 ? places.find((p) => p.key === e.key) : undefined;
      gPressedAt = 0;
      if (place) {
        e.preventDefault();
        void navigate({ to: place.to });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, togglePalette, toggleRail, showShortcuts, places]);
}

/** A single-key shortcut of the screen on display (`/` focuses the search). */
export function useScreenShortcut(key: string, action: () => void): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== key || !plainKey(e)) return;
      e.preventDefault();
      action();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [key, action]);
}
